import { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Link, useParams } from 'react-router-dom';

import { useMutation, useQuery } from '@apollo/client';
import { IconAlertTriangle, IconLock, IconLogin, IconWifiOff } from '@tabler/icons-react';
import { Filter, FilterableField, SaleBidderLevel, SortDirection } from '__generated__/globalTypes';
import config from 'config';
import { filtersToQueryFilters } from 'features/common-elements';
import { AuthContext, AuthContextAction } from 'context/AuthContext';
import type { FailureDescription } from 'features/irm/core';
import { keyServiceConfig } from 'features/irm/irmConfig';
import { PiiNoticeModal } from 'features/irm/PiiNoticeModal';
import { formatOpenedAt } from 'features/irm/StampOverlay';
import { StatusCard } from 'features/irm/StatusCard';
import { BaseFiltersConfig, Button, FilterValue, PillFilters, SpinnerComponent } from 'ui-kit';
import { performLogout } from 'utils/authUtils';

import {
  OpenSalePoolLoanTable,
  OpenSalePoolLoanTableVariables,
} from 'mutation/__generated__/salePoolLoanTable';
import { OPEN_SALE_POOL_LOAN_TABLE } from 'mutation/salePoolLoanTable';
import { COMPANY_SALES_QUERY, OPEN_SALES_QUERY } from 'query/sales';
import { CompanySales, OpenSales } from 'query/__generated__/sales';
import {
  SalePoolLoans,
  SalePoolLoansVariables,
  SalePoolLoanTerms,
  SalePoolLoanTermsVariables,
} from 'query/__generated__/salePoolLoans';
import { SALE_POOL_LOAN_TERMS_QUERY, SALE_POOL_LOANS_QUERY } from 'query/salePoolLoans';

import { ProtectedLoanArea, useBlockedEventLog } from './ProtectedLoanArea';
import { BidderDataRoom } from './BidderDataRoom';
import { ViewToggle } from './SaleApprovalPanel';
import { SalePoolLoanSortState, SalePoolLoanTable } from './SalePoolLoanTable';
import { loanPageError, type LoanPageError, VIEW_CHECK_FAILED_COPY } from './loanPageError';
import { saleLoanFiltersConfig, saleLoanTermFields } from './saleLoanFilters';
import { SaleSection, saleSectionPath } from './saleSections';
import { SectionPanel, SectionTabs, useSectionParam } from './SectionTabs';
import { PRIMARY_BUTTON_LIGHT_THEME } from './styles';
import { getBidderAccessEndsAt } from './salesUtils';
import { useBidderLoanTableSession } from './useBidderLoanTableSession';

export const LOANS_FAILED_COPY = "Couldn't load this pool's loans.";
export const SALE_LOAD_FAILED_COPY = "Couldn't load this sale.";
export const FILTER_OPTIONS_FAILED_COPY = "Couldn't load the filter options.";
export const NOTICE_DECLINED_COPY =
  "This pool's loan data includes personal information, so it opens only after you accept the notice.";
const DEFAULT_PAGE = { pageNumber: 1, pageSize: 50 };

interface AllowedFieldLists {
  filter: FilterableField[];
  terms: FilterableField[];
}

const sameLists = (a: AllowedFieldLists, b: AllowedFieldLists) =>
  a.filter.join() === b.filter.join() && a.terms.join() === b.terms.join();

const stampText = (viewerName: string, saleKey: string, openedAt: string) =>
  [viewerName, `Sale ${saleKey}`, formatOpenedAt(openedAt)].filter(Boolean).join(' · ');

const levelLabel = (level: SaleBidderLevel) =>
  level === SaleBidderLevel.L2 ? 'Level 2: includes personal information' : 'Level 1';

const formatCountdown = (msLeft: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(msLeft / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const mmss = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return hours > 0 ? `${hours}:${mmss}` : mmss;
};

// The key service ends a view at the sooner of its session limit and the bidder's access end, so
// this counts down the view, not the access.
const ViewCountdown: FC<{ endsAtWallMs: number }> = ({ endsAtWallMs }) => {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return (
    <span data-testid="loan-table-countdown" className="light:text-gray-900">
      This view closes in {formatCountdown(endsAtWallMs - now)}
    </span>
  );
};

const isRetryable = (error: LoanPageError) =>
  error.kind === 'unconfirmed' || (error.kind === 'failed' && error.retryable);

const PoolLoans: FC<{
  saleId: string;
  offeringId: string;
  view: { viewId: string; jti: string } | null;
  piiNoticeVersion?: string;
  viewAs?: SaleBidderLevel;
  /** Closes the bidder's view when api refuses a page of it; the seller has none to close. */
  onRefused?: (failure: FailureDescription) => void;
}> = ({ saleId, offeringId, view, piiNoticeVersion, viewAs, onRefused }) => {
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [sort, setSort] = useState<SalePoolLoanSortState | null>(null);
  const [filters, setFilters] = useState<FilterValue<BaseFiltersConfig>[]>([]);
  const accessVariables = {
    saleId,
    offeringId,
    ...(view && { viewId: view.viewId, viewJti: view.jti }),
    ...(piiNoticeVersion !== undefined && { piiNoticeAcknowledged: true, piiNoticeVersion }),
    ...(viewAs && { viewAs }),
  };

  const [lists, setLists] = useState<AllowedFieldLists>();
  const termFields = useMemo(
    () => (lists ? saleLoanTermFields(lists.filter, lists.terms) : []),
    [lists],
  );
  const {
    data: termsData,
    error: termsError,
    refetch: refetchTerms,
  } = useQuery<SalePoolLoanTerms, SalePoolLoanTermsVariables>(SALE_POOL_LOAN_TERMS_QUERY, {
    variables: { ...accessVariables, fields: termFields },
    skip: termFields.length === 0,
    fetchPolicy: 'no-cache',
  });
  const filtersConfig = useMemo(() => {
    const terms = new Map(
      (termsData?.salePoolLoanTerms ?? []).map(({ fieldName, terms }) => [fieldName, terms]),
    );
    return lists ? saleLoanFiltersConfig(lists.filter, lists.terms, terms) : {};
  }, [lists, termsData]);
  const queryFilters = filtersToQueryFilters(filters, filtersConfig) as Filter[];

  const { data, previousData, loading, error, refetch } = useQuery<
    SalePoolLoans,
    SalePoolLoansVariables
  >(SALE_POOL_LOANS_QUERY, {
    variables: {
      ...accessVariables,
      pagination: { page_size: page.pageSize, offset: (page.pageNumber - 1) * page.pageSize },
      ...(queryFilters.length > 0 && { filters: queryFilters }),
      ...(sort && {
        sort: {
          field: sort.field,
          direction: sort.desc ? SortDirection.desc : SortDirection.asc,
        },
      }),
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
  });
  const result = (data ?? previousData)?.salePoolLoans;
  const pageError = error ? loanPageError(error) : undefined;
  const termsLock = termsError ? loanPageError(termsError) : undefined;
  const lockFailure =
    (pageError?.kind === 'lock' && pageError.failure) ||
    (termsLock?.kind === 'lock' && termsLock.failure) ||
    undefined;
  useEffect(() => {
    if (lockFailure && onRefused) onRefused(lockFailure);
  }, [lockFailure, onRefused]);
  useEffect(() => {
    if (!result) return;
    const next = { filter: result.allowed_filter_fields, terms: result.allowed_term_fields };
    setLists((current) => (current && sameLists(current, next) ? current : next));
  }, [result]);

  const onPageChange = useCallback(
    (pageNumber: number, pageSize: number) => setPage({ pageNumber, pageSize }),
    [],
  );
  const onSortChange = useCallback((next: SalePoolLoanSortState | null) => {
    setSort(next);
    setPage((current) => ({ ...current, pageNumber: 1 }));
  }, []);
  // Only a change to what is sent goes back to the first page: an added but still empty pill
  // asks for nothing new.
  const onFiltersChange = useCallback(
    (next: FilterValue<BaseFiltersConfig>[]) => {
      setFilters(next);
      const sent = (values: FilterValue<BaseFiltersConfig>[]) =>
        JSON.stringify(filtersToQueryFilters(values, filtersConfig));
      if (sent(next) !== sent(filters)) {
        setPage((current) => ({ ...current, pageNumber: 1 }));
      }
    },
    [filters, filtersConfig],
  );

  if (lockFailure && onRefused) return null;
  // The seller has no view to close, so a refusal stays here, in api's words.
  if (lockFailure) {
    return (
      <div role="alert" className="flex flex-col gap-1 p-4 text-sm">
        <span className="font-medium text-status-error light:text-error-muted">
          {lockFailure.title}
        </span>
        <span className="text-gray-300 light:text-gray-700">{lockFailure.body}</span>
      </div>
    );
  }
  // A failed request shows no rows: the previous page's would not match the filters, sort or
  // page asked for.
  if (pageError) {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-3 p-4 text-sm">
        <span className="text-status-error light:text-error-muted">
          {pageError.kind === 'unconfirmed' ? VIEW_CHECK_FAILED_COPY : LOANS_FAILED_COPY}
        </span>
        {isRetryable(pageError) && (
          <Button
            label="Try again"
            size="small"
            className={PRIMARY_BUTTON_LIGHT_THEME}
            onClick={() => refetch().catch(() => undefined)}
          >
            Try again
          </Button>
        )}
      </div>
    );
  }
  if (!result) {
    return loading ? (
      <p className="p-4 text-sm text-gray-400 light:text-gray-700">Loading loans…</p>
    ) : (
      <p className="p-4 text-sm text-status-error light:text-error-muted">{LOANS_FAILED_COPY}</p>
    );
  }
  return (
    <>
      <div
        className="border-b border-slate-500 p-2 light:border-gray-200"
        data-testid="loan-table-filters"
      >
        <PillFilters
          className="light:text-gray-700"
          filtersConfig={filtersConfig}
          currentFilters={filters}
          setCurrentFilters={onFiltersChange}
          isLoading={loading}
        />
        {termsLock && (
          <div role="alert" className="flex flex-wrap items-center gap-3 px-2 pt-2 text-sm">
            <span className="text-status-error light:text-error-muted">
              {FILTER_OPTIONS_FAILED_COPY}
            </span>
            {isRetryable(termsLock) && (
              <Button
                label="Try again"
                size="small"
                className={PRIMARY_BUTTON_LIGHT_THEME}
                onClick={() => refetchTerms().catch(() => undefined)}
              >
                Try again
              </Button>
            )}
          </div>
        )}
      </div>
      <SalePoolLoanTable
        loans={result.loans}
        level={result.level}
        allowedSortFields={result.allowed_sort_fields}
        totalCount={result.total_count}
        page={page}
        onPageChange={onPageChange}
        sort={sort}
        onSortChange={onSortChange}
      />
    </>
  );
};

const useSignInAgain = () => {
  const { dispatch } = useContext(AuthContext);
  return useCallback(async () => {
    const logoutUrl = await performLogout(config.website.URL);
    dispatch({ type: AuthContextAction.SIGN_OUT });
    if (logoutUrl) {
      window.location.href = logoutUrl;
    }
  }, [dispatch]);
};

const BackToSale: FC<{ saleId: string }> = ({ saleId }) => (
  <Link
    to={saleSectionPath(saleId, SaleSection.POOLS)}
    className="text-sm font-medium text-purple-300 hover:underline light:text-purple-700"
  >
    Back to the sale
  </Link>
);

const LockedCard: FC<{ saleId: string; failure: FailureDescription; onRetry: () => void }> = ({
  saleId,
  failure,
  onRetry,
}) => {
  const signInAgain = useSignInAgain();
  const back = <BackToSale saleId={saleId} />;
  if (failure.next === 'sign-in') {
    return (
      <StatusCard
        tone="info"
        icon={<IconLogin size={28} />}
        title={failure.title}
        announce="alert"
        actions={
          <>
            <Button
              label="Sign in again"
              className={PRIMARY_BUTTON_LIGHT_THEME}
              onClick={signInAgain}
            >
              Sign in again
            </Button>
            {back}
          </>
        }
      >
        {failure.body}
      </StatusCard>
    );
  }
  return (
    <StatusCard
      tone={failure.next === 'retry' ? 'warning' : 'error'}
      icon={failure.next === 'retry' ? <IconWifiOff size={28} /> : <IconLock size={28} />}
      title={failure.title}
      announce="alert"
      actions={
        <>
          {failure.next === 'retry' && (
            <Button label="Open again" className={PRIMARY_BUTTON_LIGHT_THEME} onClick={onRetry}>
              Open again
            </Button>
          )}
          {back}
        </>
      }
    >
      {failure.body}
    </StatusCard>
  );
};

const BidderPoolLoans: FC<{
  saleId: string;
  offeringId: string;
  level: SaleBidderLevel | null;
  accessEndsAt?: string;
}> = ({ saleId, offeringId, level, accessEndsAt }) => {
  const { state, acceptNotice, declineNotice, closeRefusedNotice, reopen, lock } =
    useBidderLoanTableSession(saleId, offeringId, level, keyServiceConfig);
  const viewId = state.name === 'open' ? state.view.viewId : null;
  const recordBlocked = useBlockedEventLog(viewId);

  switch (state.name) {
    case 'unconfigured':
      return (
        <StatusCard
          tone="warning"
          icon={<IconAlertTriangle size={28} />}
          title="Protected loan data is not available here"
          announce="alert"
          actions={<BackToSale saleId={saleId} />}
        >
          This environment isn’t set up to show protected loan data.
        </StatusCard>
      );
    case 'notice':
      return (
        <PiiNoticeModal
          onAccept={acceptNotice}
          onDecline={declineNotice}
          onRefusalClosed={closeRefusedNotice}
        />
      );
    case 'declined':
      return (
        <StatusCard
          tone="info"
          icon={<IconLock size={28} />}
          title="Accept the notice to view these loans"
          actions={
            <>
              <Button
                label="Show the notice again"
                className={PRIMARY_BUTTON_LIGHT_THEME}
                onClick={reopen}
              >
                Show the notice again
              </Button>
              <BackToSale saleId={saleId} />
            </>
          }
        >
          {NOTICE_DECLINED_COPY}
        </StatusCard>
      );
    case 'opening':
      return (
        <div
          role="status"
          className="mt-8 flex flex-col items-center gap-3 text-gray-300 light:text-gray-700"
        >
          <SpinnerComponent loading positionRelative />
          <p>Confirming your access to this pool’s loan data…</p>
        </div>
      );
    case 'stopped':
      return <LockedCard saleId={saleId} failure={state.failure} onRetry={reopen} />;
    case 'open': {
      const { view, session } = state;
      const viewLevel = view.level === 2 ? SaleBidderLevel.L2 : SaleBidderLevel.L1;
      return (
        <>
          <h2 className="font-heebo text-lg font-medium text-white light:text-gray-900">
            {session.offering_name}
          </h2>
          <ProtectedLoanArea
            stamp={stampText(view.viewerName, view.saleKey, view.openedAt)}
            suspended={state.suspended}
            onBlocked={recordBlocked}
            header={
              <>
                <span>Opened by {view.viewerName}</span>
                <span>Sale {view.saleKey}</span>
                <span>Pool {view.poolNumber}</span>
                <span>{formatOpenedAt(view.openedAt)}</span>
                <span>{levelLabel(viewLevel)}</span>
                <ViewCountdown endsAtWallMs={state.accessEndsAtWallMs} />
                {accessEndsAt && (
                  <span data-testid="loan-table-access-end">
                    Access ends {formatOpenedAt(accessEndsAt)}
                  </span>
                )}
              </>
            }
          >
            <PoolLoans
              saleId={saleId}
              offeringId={offeringId}
              view={{ viewId: view.viewId, jti: view.jti }}
              piiNoticeVersion={state.piiNoticeVersion}
              onRefused={lock}
            />
          </ProtectedLoanArea>
        </>
      );
    }
  }
};

// The seller sees its own pool, so no IRM session or access log: only the watermark and the
// copy blocking, with a preview of what each bidder level is shown.
const SellerPoolLoans: FC<{ saleId: string; offeringId: string }> = ({ saleId, offeringId }) => {
  const [viewAs, setViewAs] = useState<SaleBidderLevel>(SaleBidderLevel.L2);
  const [openLoanTable, { data, error }] = useMutation<
    OpenSalePoolLoanTable,
    OpenSalePoolLoanTableVariables
  >(OPEN_SALE_POOL_LOAN_TABLE);
  const recordBlocked = useBlockedEventLog(null);

  const open = useCallback(
    () =>
      openLoanTable({ variables: { saleId, offeringId, viewAs: SaleBidderLevel.L2 } }).catch(
        () => undefined,
      ),
    [offeringId, openLoanTable, saleId],
  );
  useEffect(() => {
    void open();
  }, [open]);

  const session = data?.openSalePoolLoanTable;
  if (error) {
    const failure = loanPageError(error);
    return (
      <StatusCard
        tone="error"
        icon={<IconLock size={28} />}
        title="This loan data can't be opened"
        announce="alert"
        actions={
          <>
            {isRetryable(failure) && (
              <Button label="Try again" className={PRIMARY_BUTTON_LIGHT_THEME} onClick={open}>
                Try again
              </Button>
            )}
            <BackToSale saleId={saleId} />
          </>
        }
      >
        {failure.kind === 'lock' ? failure.failure.body : LOANS_FAILED_COPY}
      </StatusCard>
    );
  }
  if (!session) {
    return <p className="mt-6 text-sm text-gray-400 light:text-gray-700">Loading loans…</p>;
  }
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heebo text-lg font-medium text-white light:text-gray-900">
          {session.offering_name}
        </h2>
        <ViewToggle viewAs={viewAs} onChange={setViewAs} label="Loan table view" />
      </div>
      <ProtectedLoanArea
        stamp={stampText(session.viewer_name, session.sale_key, session.opened_at)}
        onBlocked={recordBlocked}
        header={
          <>
            <span>Seller preview by {session.viewer_name}</span>
            <span>Sale {session.sale_key}</span>
            <span>Pool {session.pool_number}</span>
            <span>{formatOpenedAt(session.opened_at)}</span>
            <span>Showing {levelLabel(viewAs)}</span>
          </>
        }
      >
        <PoolLoans
          key={viewAs}
          saleId={saleId}
          offeringId={offeringId}
          view={null}
          viewAs={viewAs}
        />
      </ProtectedLoanArea>
    </>
  );
};

export enum PoolSection {
  LOANS = 'loans',
  DOCUMENTS = 'documents',
}

const POOL_SECTION_LABEL: Record<PoolSection, string> = {
  [PoolSection.LOANS]: 'Loans',
  [PoolSection.DOCUMENTS]: 'Documents',
};

const Page: FC<{ children: ReactNode }> = ({ children }) => (
  <div
    className="mt-3 h-full min-w-0 rounded-lg bg-background-surface p-6 text-white light:bg-white light:text-gray-900"
    style={{ gridArea: 'page' }}
  >
    {children}
  </div>
);

// Loans unless the URL asks for documents, so the sale page and a shared link can open either.
const PoolSections: FC<{ saleName: string; panels: SectionPanel<PoolSection>[] }> = ({
  saleName,
  panels,
}) => {
  const [section, setSection] = useSectionParam(panels.map((panel) => panel.section));
  return (
    <>
      <h1 className="mt-2 font-heebo text-2xl font-semibold text-purple-300 light:text-purple-600">
        {saleName}: pool {section}
      </h1>
      <SectionTabs
        label="Pool section"
        idPrefix="pool-section"
        className="mt-4"
        panels={panels}
        active={section}
        onChange={setSection}
      />
    </>
  );
};

const poolPanel = (section: PoolSection, render: () => ReactNode): SectionPanel<PoolSection> => ({
  section,
  label: POOL_SECTION_LABEL[section],
  render: () => <div className="mt-4">{render()}</div>,
});

export const SalePoolLoansPage: FC = () => {
  const { sale_id: saleId = '', offering_id: offeringId = '' } = useParams<{
    sale_id: string;
    offering_id: string;
  }>();
  const {
    data: openData,
    loading: openLoading,
    error: openError,
    refetch: refetchOpen,
  } = useQuery<OpenSales>(OPEN_SALES_QUERY);
  const {
    data: companyData,
    loading: companyLoading,
    error: companyError,
    refetch: refetchCompany,
  } = useQuery<CompanySales>(COMPANY_SALES_QUERY);

  if (openLoading || companyLoading) {
    return <Page>{null}</Page>;
  }
  // companySales first, as on SalePage: a seller's own open sale is in both lists.
  const sale =
    companyData?.companySales.find((s) => s.sale_id === saleId) ??
    openData?.openSales.find((s) => s.sale_id === saleId);
  // As on SalePage: a sale one list returned still shows when the other failed, but a sale
  // neither list returned is unknown, not missing, while a list that could hold it failed.
  if (!sale && ((openError && !openData) || (companyError && !companyData))) {
    return (
      <Page>
        <div role="alert" className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-status-error light:text-error-muted">{SALE_LOAD_FAILED_COPY}</span>
          <Button
            label="Try again"
            size="small"
            className={PRIMARY_BUTTON_LIGHT_THEME}
            onClick={() => {
              refetchOpen().catch(() => undefined);
              refetchCompany().catch(() => undefined);
            }}
          >
            Try again
          </Button>
        </div>
      </Page>
    );
  }
  if (!sale) {
    return (
      <Page>
        <h1 className="font-heebo text-lg font-medium">Sale not found</h1>
      </Page>
    );
  }
  const isSeller = !('bidder_status' in sale);
  const bidderLevel = 'bidder_level' in sale ? sale.bidder_level : null;
  const accessEndsAt = getBidderAccessEndsAt(sale.rounds);

  return (
    <Page>
      <BackToSale saleId={saleId} />
      <PoolSections
        saleName={sale.sale_name}
        panels={
          isSeller
            ? [
                poolPanel(PoolSection.LOANS, () => (
                  <SellerPoolLoans saleId={saleId} offeringId={offeringId} />
                )),
              ]
            : [
                poolPanel(PoolSection.LOANS, () => (
                  <BidderPoolLoans
                    key={`${saleId}:${offeringId}`}
                    saleId={saleId}
                    offeringId={offeringId}
                    level={bidderLevel}
                    accessEndsAt={accessEndsAt}
                  />
                )),
                poolPanel(PoolSection.DOCUMENTS, () => (
                  <BidderDataRoom
                    saleId={saleId}
                    saleKeys={sale.sale_keys}
                    level={bidderLevel}
                    accessEndsAt={accessEndsAt}
                    downloadPeriodHours={sale.download_period_hours}
                    offeringId={offeringId}
                  />
                )),
              ]
        }
      />
    </Page>
  );
};
